import { Fragment, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { css, cx } from "styled-system/css";
import { RefreshCw } from "lucide-react";
import { ak } from "./admin-styles";
import { Banner, Modal, PageHead, Section, Status, Time } from "~/components/editorial";
import { SyncLogTable } from "./sync-parts";
import { instanceStatus } from "./sync-status";
import { api } from "~/lib/api";

interface CursorInstance {
  id: string;
  name: string;
  teamId: string;
  connectedAt: string;
  lastSyncAt: string | null;
  syncStatus: "idle" | "syncing" | "error" | "pending";
  syncProgress?: number;
  syncMessage?: string | null;
  syncError?: string | null;
  estimatedTimeRemaining?: number | null;
}

interface SyncLog {
  id: string;
  sourceName: string;
  syncType: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  duration: number | null;
  itemsSynced: number;
  errorMessage: string | null;
}

export function CursorSettingsPage() {
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    teamId: "",
    apiKey: "",
  });

  // Update credentials form state
  const [updateFormData, setUpdateFormData] = useState({
    name: "",
    apiKey: "",
  });

  const queryClient = useQueryClient();
  const instancesQuery = useQuery({
    queryKey: ["cursor-instances"],
    queryFn: async () =>
      (await api.get<{ data: CursorInstance[] }>("/cursor/instances")).data,
    staleTime: 0,
    // Poll for updates while any instance is syncing
    refetchInterval: (query) =>
      query.state.data?.some(
        (inst) =>
          inst.syncStatus === "syncing" || inst.syncStatus === "pending",
      )
        ? 2000
        : false,
  });
  const instances = instancesQuery.data ?? [];
  const loading = instancesQuery.isLoading;
  const fetchInstances = () => instancesQuery.refetch();
  const setInstances = (update: (prev: CursorInstance[]) => CursorInstance[]) =>
    queryClient.setQueryData<CursorInstance[]>(["cursor-instances"], (prev) =>
      update(prev ?? []),
    );

  const { data: syncLogs = [] } = useQuery({
    queryKey: ["cursor-sync-logs"],
    queryFn: async () =>
      (await api.get<{ data: SyncLog[] }>("/cursor/sync-logs?limit=20")).data,
    staleTime: 0,
  });

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    setConnecting(true);
    setError(null);

    try {
      await api.post("/cursor/instances", {
        name: formData.name || `Cursor Team ${formData.teamId.slice(0, 8)}`,
        teamId: formData.teamId.trim(),
        apiKey: formData.apiKey.trim(),
      });

      setSuccess("Cursor team connected. Initial sync is starting...");
      setShowConnectModal(false);
      setFormData({ name: "", teamId: "", apiKey: "" });
      fetchInstances();

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to connect Cursor team",
      );
    } finally {
      setConnecting(false);
    }
  };

  const handleSync = async (instanceId: string, full = false) => {
    // Optimistically update state immediately
    setInstances((prev) =>
      prev.map((inst) =>
        inst.id === instanceId
          ? { ...inst, syncStatus: "pending" as const }
          : inst,
      ),
    );

    try {
      await api.post(
        `/cursor/instances/${instanceId}/sync${full ? "?full=true" : ""}`,
      );
      fetchInstances();
    } catch (err) {
      console.error("Failed to trigger sync:", err);
      fetchInstances();
    }
  };

  const handleDelete = async (instanceId: string) => {
    if (
      !confirm(
        "Are you sure you want to disconnect this Cursor team? All synced usage data will be deleted.",
      )
    ) {
      return;
    }
    try {
      await api.delete(`/cursor/instances/${instanceId}`);
      setInstances((prev) => prev.filter((inst) => inst.id !== instanceId));
    } catch (err) {
      console.error("Failed to delete instance:", err);
    }
  };

  const handleUpdateCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showUpdateModal) return;

    setUpdating(true);
    setError(null);

    try {
      await api.patch(`/cursor/instances/${showUpdateModal}`, {
        name: updateFormData.name || undefined,
        apiKey: updateFormData.apiKey || undefined,
      });

      setSuccess("Settings updated.");
      setShowUpdateModal(null);
      setUpdateFormData({ name: "", apiKey: "" });
      fetchInstances();

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update settings",
      );
    } finally {
      setUpdating(false);
    }
  };

  const failingInstances = instances.filter(
    (i) => i.syncStatus === "error",
  ).length;

  return (
    <div className={ak.page}>
      <PageHead
        back={{ to: "/settings", label: "Settings" }}
        kicker="Admin · Cursor"
        title="Cursor"
        dek={
          loading ? (
            "AI usage and team spend from the Cursor admin API."
          ) : instances.length === 0 ? (
            "No Cursor teams connected yet."
          ) : (
            <>
              <span className={ak.mono}>{instances.length}</span>{" "}
              {instances.length === 1 ? "team" : "teams"} connected.{" "}
              {failingInstances === 0 ? (
                "No sync is failing."
              ) : (
                <>
                  <span className={ak.mono}>{failingInstances}</span>{" "}
                  {failingInstances === 1 ? "is" : "are"} failing.
                </>
              )}
            </>
          )
        }
      />

      {success && <Banner>{success}</Banner>}

      {error && (
        <Banner tone="caution" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      <Section
        kicker="I. Teams"
        title="Connected Cursor teams"
        actions={
          <button className={ak.btn} onClick={() => setShowConnectModal(true)}>
            Connect team
          </button>
        }
      >
        {loading ? (
          <div className={ak.loading}>Loading…</div>
        ) : instances.length > 0 ? (
          <div className={ak.tableWrap}>
            <table className={cx(ak.table, styles.detailTable)}>
              <thead>
                <tr>
                  <th>Team</th>
                  <th>Team ID</th>
                  <th>Status</th>
                  <th>Last synced</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {instances.map((instance) => {
                  const st = instanceStatus(instance);
                  const busy =
                    instance.syncStatus === "syncing" ||
                    instance.syncStatus === "pending";
                  const hasDetail =
                    instance.syncStatus === "syncing" ||
                    (instance.syncStatus === "error" && !!instance.syncError);
                  return (
                    <Fragment key={instance.id}>
                      <tr data-has-detail={hasDetail || undefined}>
                        <td>
                          <div className={styles.name}>{instance.name}</div>
                        </td>
                        <td className={ak.mono} title={instance.teamId}>
                          {instance.teamId.length > 12
                            ? `${instance.teamId.slice(0, 12)}…`
                            : instance.teamId}
                        </td>
                        <td>
                          <Status kind={st.kind}>{st.label}</Status>
                        </td>
                        <td>
                          {instance.lastSyncAt ? (
                            <Time iso={instance.lastSyncAt} />
                          ) : (
                            <span className={ak.muted}>never</span>
                          )}
                        </td>
                        <td>
                          <div className={ak.actions}>
                            <button
                              className={cx(ak.btnSecondary, ak.btnSm)}
                              onClick={() => handleSync(instance.id)}
                              disabled={busy}
                              title="Quick sync: last 7 days"
                            >
                              <RefreshCw
                                size={12}
                                className={
                                  instance.syncStatus === "syncing"
                                    ? ak.spinning
                                    : ""
                                }
                              />
                              Sync
                            </button>
                            <button
                              className={cx(ak.btnSecondary, ak.btnSm)}
                              onClick={() => handleSync(instance.id, true)}
                              disabled={busy}
                              title="Full sync: last 30 days"
                            >
                              Full
                            </button>
                            <button
                              className={cx(ak.btnSecondary, ak.btnSm)}
                              onClick={() => setShowUpdateModal(instance.id)}
                              title="Update name or API key"
                            >
                              Settings
                            </button>
                            <button
                              className={cx(ak.btnDanger, ak.btnSm)}
                              onClick={() => handleDelete(instance.id)}
                              disabled={instance.syncStatus === "syncing"}
                              title="Disconnect"
                            >
                              Disconnect
                            </button>
                          </div>
                        </td>
                      </tr>
                      {hasDetail && (
                        <tr data-detail>
                          <td colSpan={5}>
                            {instance.syncStatus === "syncing" && (
                              <div className={styles.progressBlock}>
                                <div className={ak.progress}>
                                  <div
                                    style={{
                                      width: `${instance.syncProgress || 0}%`,
                                    }}
                                  />
                                </div>
                                <div>
                                  {instance.syncMessage || "Syncing…"}{" "}
                                  <span className={ak.mono}>
                                    {instance.syncProgress || 0}%
                                  </span>
                                </div>
                              </div>
                            )}
                            {instance.syncStatus === "error" &&
                              instance.syncError && (
                                <Status kind="fail" className={styles.wrap}>
                                  {instance.syncError}
                                </Status>
                              )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={ak.empty}>
            <p>No Cursor teams connected.</p>
            <p>Connect a team to count AI-assisted code and spend.</p>
          </div>
        )}

        <p className={ak.note}>
          ● connected, ◆ syncing, ◇ queued, ▲ failing. Cursor syncs the last 30
          days of usage; its API only accepts 30-day ranges.
        </p>
      </Section>

      <Section kicker="II. History" title="Recent syncs">
        <SyncLogTable logs={syncLogs} itemsLabel="Records" />
      </Section>

      <Section kicker="III. Setup" title="Connecting a team">
        <ol className={ak.steps}>
          <li>
            Open{" "}
            <a
              href="https://cursor.com/settings"
              target="_blank"
              rel="noopener noreferrer"
              className={ak.link}
            >
              Cursor settings
            </a>
            .
          </li>
          <li>Go to your team settings (Team or Enterprise plan).</li>
          <li>Open the API Keys or Admin API section.</li>
          <li>Create an API key and copy it.</li>
          <li>Find the Team ID in the team settings URL or info panel.</li>
          <li>Click Connect team above and enter both.</li>
        </ol>
        <p className={ak.note}>
          <strong>Note:</strong> the Cursor Analytics API needs a Team or
          Enterprise plan. Commit attribution may need Enterprise.
        </p>
      </Section>

      {/* Connect Modal */}
      {showConnectModal && (
        <Modal
          title="Connect a Cursor team"
          onClose={() => setShowConnectModal(false)}
        >
          <form onSubmit={handleConnect}>
            <div className={ak.field}>
              <label className={ak.label} htmlFor="cursor-name">
                Team name (optional)
              </label>
              <input
                id="cursor-name"
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="My Cursor Team"
                className={ak.input}
              />
            </div>
            <div className={ak.field}>
              <label className={ak.label} htmlFor="cursor-team">
                Team ID
              </label>
              <input
                id="cursor-team"
                type="text"
                value={formData.teamId}
                onChange={(e) =>
                  setFormData({ ...formData, teamId: e.target.value })
                }
                placeholder="your-team-id"
                className={cx(ak.input, ak.mono)}
                required
              />
              <p className={ak.hint}>
                In your Cursor team settings URL or info panel.
              </p>
            </div>
            <div className={ak.field}>
              <label className={ak.label} htmlFor="cursor-key">
                API key
              </label>
              <input
                id="cursor-key"
                type="password"
                value={formData.apiKey}
                onChange={(e) =>
                  setFormData({ ...formData, apiKey: e.target.value })
                }
                placeholder="key_…"
                className={cx(ak.input, ak.mono)}
                required
              />
              <p className={ak.hint}>
                Create it in Cursor team settings, under API Keys.
              </p>
            </div>
            <div className={ak.modalActions}>
              <button
                type="button"
                onClick={() => setShowConnectModal(false)}
                className={ak.btnSecondary}
              >
                Cancel
              </button>
              <button type="submit" disabled={connecting} className={ak.btn}>
                {connecting ? "Connecting…" : "Connect"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Update Modal */}
      {showUpdateModal && (
        <Modal
          title="Update team settings"
          onClose={() => setShowUpdateModal(null)}
        >
          <form onSubmit={handleUpdateCredentials}>
            <div className={ak.field}>
              <label className={ak.label} htmlFor="cursor-update-name">
                Team name
              </label>
              <input
                id="cursor-update-name"
                type="text"
                value={updateFormData.name}
                onChange={(e) =>
                  setUpdateFormData({
                    ...updateFormData,
                    name: e.target.value,
                  })
                }
                placeholder="Leave blank to keep current"
                className={ak.input}
              />
            </div>
            <div className={ak.field}>
              <label className={ak.label} htmlFor="cursor-update-key">
                New API key
              </label>
              <input
                id="cursor-update-key"
                type="password"
                value={updateFormData.apiKey}
                onChange={(e) =>
                  setUpdateFormData({
                    ...updateFormData,
                    apiKey: e.target.value,
                  })
                }
                placeholder="Leave blank to keep current"
                className={cx(ak.input, ak.mono)}
              />
            </div>
            <div className={ak.modalActions}>
              <button
                type="button"
                onClick={() => setShowUpdateModal(null)}
                className={ak.btnSecondary}
              >
                Cancel
              </button>
              <button type="submit" disabled={updating} className={ak.btn}>
                {updating ? "Updating…" : "Update"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

const styles = {
  detailTable: css({
    "& tr[data-has-detail] td": { borderBottom: "none" },
    "& tr[data-detail] td": { pt: "0", pb: "12px", fontSize: "12.5px" },
  }),
  name: css({
    fontFamily: "serif",
    fontSize: "16px",
    fontWeight: 700,
    lineHeight: 1.2,
  }),
  progressBlock: css({
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    color: "ed.ink2",
  }),
  wrap: css({ whiteSpace: "normal" }),
};
